import Axios from 'axios'
import { XsrfResilient } from './Helpers'
import { XsrfResilient as TypedXsrfResilient } from './TypedHelpers'
import { CheckoutType } from '@/types/Checkout'
import { BigCommerceCart, BOPUSInfo } from "@/types/Cart";
import { error, log } from './Log';

export type addToCartSuccess = { success: true, newCart: BigCommerceCart }
export type addToCartFailed = { success: false }
export type addToCartType = (p: {
  productId: number,
  quantity: number,
  variantId: number,
}) => Promise<addToCartSuccess | addToCartFailed>

const getCart = async () => {
  const obj = (await Axios.get<Array<BigCommerceCart> | null>('/api/storefront/cart?include=lineItems.physicalItems.options')).data
  return Array.isArray(obj) ? obj[0] : obj
}
const addToCart: addToCartType = async ({
  productId,
  quantity,
  variantId,
}) => {
  let newCart: BigCommerceCart | null = null, success
  try {
    log('regularAddToCartFlow start', { productId, quantity, variantId, })
    const cart = await getCart()
    const itemAlreadyInTheCart = cart?.lineItems?.physicalItems?.find(p => p?.productId === productId && p.variantId === variantId)
    let cartId = cart?.id || ''    
    if (!cartId) { // Create New Cart
      const { status, data } = await Axios.post<BigCommerceCart>(`/api/storefront/carts?include=lineItems.physicalItems.options`, {
        "lineItems": [
          {
            quantity,
            productId,
            variantId,
          }
        ],
        "locale": "en"
      })
      success = isSuccessStatus(status)
      newCart = data
    } else if (itemAlreadyInTheCart) { // update Item to the cart
      const { status, data } = await Axios.put<BigCommerceCart>(`/api/storefront/carts/${cartId}/items/${itemAlreadyInTheCart.id}?include=lineItems.physicalItems.options`, {
        "lineItem": {
          productId,
          variantId,
          quantity: quantity + itemAlreadyInTheCart.quantity,
        }
      })
      success = isSuccessStatus(status)
      newCart = data
    } else { // new Item to the cart
      const { status, data } = await Axios.post<BigCommerceCart>(`/api/storefront/carts/${cartId}/items?include=lineItems.physicalItems.options`, {
        "lineItems": [
          {
            quantity,
            productId,
            variantId,
          }
        ],
        "locale": "en"
      })
      success = isSuccessStatus(status)
      newCart = data
    }
  } catch (err) {
    error('Failed to add item to cart', { err })
  } finally {
    log('regularAddToCartFlow End', { newCart })
  }
  if (success && !!newCart) {
    return { success, newCart }
  }
  return { success: false }
}
export type CartOperator = (v:{
  cartId: string,
  itemId: string,
  productId?: number,
  variantId?: number,
  quantity?: number,
}) => Promise<addToCartSuccess | addToCartFailed>

const cartUpdateQuantity: CartOperator = async ({
  itemId,
  cartId,
  productId,
  variantId,
  quantity
}) => {
  let success = false, newCart: BigCommerceCart | null = null
  try {
    const cartUpdate = await Axios.put<BigCommerceCart>(`/api/storefront/carts/${cartId}/items/${itemId}?include=lineItems.physicalItems.options`, { "lineItem": { productId, variantId, quantity } })
    success = cartUpdate.status === 200
    newCart = cartUpdate.data
    return { success, newCart }
  } catch (err) {
    error('Failed to update cart quantity')
  }
  return { success: false }
}
const cartDeleteItem: CartOperator = async ({ cartId, itemId }: { cartId: string, itemId: string }) => {
  let success = false, newCart
  try {
    const { status, data } = await Axios.delete(`/api/storefront/carts/${cartId}/items/${itemId}?include=lineItems.physicalItems.options`)
    success = isSuccessStatus(status)
    newCart = data
  } catch (err) {
    error('Failed to update cart quantity')
  }
  return { success, newCart: success && newCart ? newCart : null }
}
const isSuccessStatus = (status: number): boolean => [200, 204].includes(status)
const Cart = {
  deleteItem: cartDeleteItem,
  getProductFromCart: ({ itemId, cart }) => {
    if (!cart) {
      return null
    }
    const findMatchingItem = (item) => item.id === itemId
    const itemType = Object.keys(cart?.lineItems || {}).find(key => cart.lineItems[key].find(findMatchingItem))
    return cart.lineItems?.[itemType || '']?.find(findMatchingItem)
  },
  updateQuantity: cartUpdateQuantity,
  content: getCart,
  add: addToCart,
  applyCoupon: XsrfResilient(async (code) => {
    const { data } = (await Axios.post('/remote/v1/apply-code', { code })).data
    return data
  }),
  removeCoupon: XsrfResilient(async (cartId, couponCode) => {
    await Axios.delete(`/api/storefront/checkouts/${cartId}/coupons/${couponCode}`)
  }),
  createCart: XsrfResilient(async (lineItems) => {
    let data = {}
    try {
      data = (await Axios.post('/api/storefront/carts', lineItems)).data
    } catch {
    }
    return data
  }),
  storefrontAddItems: XsrfResilient(async (cartId, lineItems) => {
    const { data } = (await Axios.post(`/api/storefront/carts/${cartId}/items`, { lineItems }))
    return data
  }),
  deleteCart: XsrfResilient(async (cartId) => {
    const { data } = (await Axios.delete(`/api/storefront/carts/${cartId}`))
    return data
  }),
  getBOPUSInfo: TypedXsrfResilient(async (cartId: string) => {
    if (!cartId) {
      throw new Error('Cart ID is not provided')
    }
    const checkIsBOPUSItem = (customFields) => customFields.some((field) => field.fieldValue === "1")
    const { data: { consignments } }: { data: CheckoutType } = await Axios.get(`/api/storefront/checkouts/${cartId}?include=consignments.availableShippingOptions`)
    return consignments.map((consignment) => {
      const { shippingAddress: { address1, address2, city, stateOrProvince, stateOrProvinceCode, phone, customFields, postalCode }, id, lineItemIds } = consignment
      const isBOPUS = checkIsBOPUSItem(customFields)

      const result: BOPUSInfo = {
        address1,
        address2,
        city,
        stateOrProvince,
        stateOrProvinceCode,
        postalCode,
        phone,
        id,
        isBOPUS,
        lineItemIds
      }
      return result
    })
  }),
}

export default Cart
